/** Copyright 2026 Midas Healthcare Solutions - All Rights Reserved **/
import { Injectable } from '@angular/core';
import { ApiService, LocalStorageAuthTokenKey, UiActions, User } from '@midas/shared/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch, pessimisticUpdate } from '@nrwl/angular';
import { map, of } from 'rxjs';
import * as UserActions from './user.actions';
import { Store } from '@ngrx/store';
import { TranslocoService } from '@ngneat/transloco';

@Injectable()
export class UserEffects {
  readonly getProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getProfile),
      fetch({
        id: () => true, // Makes `fetch` act like switchMap (cancels previous request)
        run: () =>
          this.api.get<User>('/user').pipe(
            map((user) => {
              return UserActions.getProfileSuccess({ user });
            }),
          ),
        onError: (action, error) => {
          LocalStorageAuthTokenKey.deleteToken();
          return of(UserActions.getProfileError({ error }));
        },
      }),
    ),
  );

  readonly setUserSmsMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.setUserSmsMfa),
      pessimisticUpdate({
        run: (dto) =>
          this.api.post('/user/set-sms-mfa', dto).pipe(
            map(() => {
              return UserActions.setUserSmsMfaSuccess();
            }),
          ),
        onError: (action, error) => {
          return UserActions.setUserSmsMfaError({ error });
        },
      }),
    ),
  );

  readonly verifyUserSmsMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.verifyUserSmsMfa),
      pessimisticUpdate({
        run: (dto) =>
          this.api.post<User>('/user/verify-sms-mfa', dto).pipe(
            map((user) => {
              return UserActions.verifyUserSmsMfaSuccess({ user });
            }),
          ),
        onError: (action, error) => {
          return UserActions.verifyUserSmsMfaError({ error });
        },
      }),
    ),
  );

  readonly disableUserMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.disableUserMfa),
      pessimisticUpdate({
        run: (dto) =>
          this.api.post<{ action: string; session: string }>('/user/disable-mfa', dto).pipe(
            map(({ action, session }) => {
              return UserActions.disableUserMfaSuccess({ action, session });
            }),
          ),
        onError: (action, error) => {
          return UserActions.disableUserMfaError({ error });
        },
      }),
    ),
  );

  readonly verifyDisableUserMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.verifyDisableUserMfa),
      pessimisticUpdate({
        run: (dto) =>
          this.api.post<{ token: string; user: User }>('/user/verify-disable-mfa', dto).pipe(
            map(({ user, token }) => {
              LocalStorageAuthTokenKey.authToken = token;
              return UserActions.verifyDisableUserMfaSuccess({ user });
            }),
          ),
        onError: (action, error) => {
          return UserActions.verifyDisableUserMfaError({ error });
        },
      }),
    ),
  );

  readonly updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfile),
      pessimisticUpdate({
        run: (dto) =>
          this.api
            .put<{
              username: string;
              firstName: string | null;
              lastName: string | null;
              email: string | null;
              cellNumber: string | null;
            }>('/user/update-profile', dto)
            .pipe(
              map(({ firstName, lastName, email, cellNumber }) => {
                this.store.dispatch(
                  UiActions.setGlobalSuccess({
                    success: this.transloco.translate('User updated successfully'),
                  }),
                );
                return UserActions.updateProfileSuccess({ firstName, lastName, email, cellNumber });
              }),
            ),
        onError: (action, error) => {
          return UserActions.updateProfileError({ error });
        },
      }),
    ),
  );

  readonly recordAlibiLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.recordAlibiLogin),
      pessimisticUpdate({
        run: (dto) =>
          this.api.post<boolean>('/user/alibi-login', {}).pipe(
            map((success) => {
              return UserActions.recordAlibiLoginSuccess({ success });
            }),
          ),
        onError: (action, error) => {
          return UserActions.recordAlibiLoginError({ error });
        },
      }),
    ),
  );

  readonly updateUserNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUserNotifications),
      pessimisticUpdate({
        run: (dto) =>
          this.api.put<User>('/user/notifications', dto).pipe(
            map((user) => {
              return UserActions.updateUserNotificationsSuccess({ user });
            }),
          ),
        onError: (action, error) => {
          return UserActions.updateUserNotificationsError({ error });
        },
      }),
    ),
  );

  readonly updateUserAlibiSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUserAlibiSettings),
      pessimisticUpdate({
        run: (dto) =>
          this.api.patch('/user/alibi-settings', dto).pipe(
            map(() => {
              return UserActions.updateUserAlibiSettingsSuccess(dto);
            }),
          ),
        onError: (action, error) => {
          // UserActions.updateUserAlibiSettingsSuccess(action);
          return UserActions.updateUserAlibiSettingsError({ error });
        },
      }),
    ),
  );

  readonly setProfilePictureUploadComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.setProfilePictureUploadComplete),
      pessimisticUpdate({
        run: () =>
          this.api.put<{ version: number }>('/user/profile-picture/complete', {}).pipe(
            map(({ version }) => {
              return UserActions.setProfilePictureUploadCompleteSuccess({ version });
            }),
          ),
        onError: (action, error) => {
          return UserActions.setProfilePictureUploadCompleteError({ error });
        },
      }),
    ),
  );

  readonly deleteProfilePicture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteProfilePicture),
      pessimisticUpdate({
        run: () =>
          this.api.delete<{ userId: number }>('/user/profile-picture').pipe(
            map(({ userId }) => {
              return UserActions.deleteProfilePictureSuccess({ userId });
            }),
          ),
        onError: (action, error) => {
          return UserActions.deleteProfilePictureError({ error });
        },
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private api: ApiService,
    private store: Store,
    private transloco: TranslocoService,
  ) {}
}
